import {
  DeduplicateRule,
  DelayRule,
  FilterRule,
  RetryRule,
  TransformRule,
} from '../../../../typings/Webhook.interface';
import { Transformation } from '../../../../typings/Transformation.interface';
import { WebhookFilterProperty } from '../../../../typings/Webhook.interface';
import HookdeckAPI from '../client/Hookdeck';
import LINKS from '../configs/links';

export const INTERVAL_TIME_UNITS = {
  seconds: 1000,
  minutes: 1000 * 60,
  hours: 1000 * 60 * 60,
  days: 1000 * 60 * 60 * 24,
};

export const RETRY_STATEGY_LABELS = {
  linear: 'Linearly',
  exponential: 'Exponentially',
};

const rule_naming = {
  retry: 'interval',
  delay: 'interval',
  deduplicate: 'window',
};
export const getMsIntervalByUnit = (
  interval: number,
  rule?: keyof typeof rule_naming,
): [number, string] => {
  for (const [unit, multiplier] of Object.entries(INTERVAL_TIME_UNITS).reverse()) {
    const unit_interval = interval / multiplier;
    if (unit_interval % 1 === 0) {
      return [unit_interval, unit];
    }
  }

  if (rule && rule_naming[rule]) {
    throw new Error(`Invalid ${rule} ${rule_naming[rule]}: ${interval}`);
  }

  throw new Error(`Invalid rule interval or window: ${interval}`);
};

const complete_rule_pattern = /^(!?[2-5]\d{2}|[<>]=?[2-5]\d{2}|[2-5]\d{2}-[2-5]\d{2})$/;

const validateHttpCodes = (value: string): string | undefined => {
  if (!value) return undefined;

  const rules = value.split(',').map((rule) => rule.trim());
  const validPartialRulePattern = /^(!|[<>]=?)?([2-5]\d{0,2})?(-[2-5]\d{0,2})?$/;

  for (const rule of rules) {
    // Ignore incomplete rules, but validate format
    if (!validPartialRulePattern.test(rule)) {
      return `Invalid rule format: ${rule}. Please use the format: >300, >=300, <400, <=400, 404, !401, 500-599`;
    }

    // Only validate complete rules
    if (complete_rule_pattern.test(rule)) {
      if (rule.includes('-')) {
        const [min, max] = rule.split('-').map(Number);
        if (min >= max) {
          return `Invalid range: ${rule}. The first number should be less than the second.`;
        }
      }

      const numericValue = rule.replace(/[!<>=]/g, '');
      const code = parseInt(numericValue, 10);
      if (code < 200 || code > 599) {
        return `Invalid HTTP code: ${numericValue}. Please use codes between 200 and 599.`;
      }
    }
  }

  return undefined;
};

const validate = {
  filter: async ({ headers, body, path, query }) => {
    let errors: {
      body?: string;
      headers?: string;
      path?: string;
      query?: string;
    } = {};
    try {
      headers && JSON.parse(headers);
    } catch (e) {
      errors.headers = 'Invalid headers JSON';
    }
    try {
      body && JSON.parse(body);
    } catch (e) {
      errors.body = 'Invalid body JSON';
    }
    try {
      path && JSON.parse(path);
    } catch (e) {
      errors.path = 'Invalid URL path';
    }
    try {
      query && JSON.parse(query);
    } catch (e) {
      errors.query = 'Invalid query JSON';
    }
    if (!headers && !body && !path && !query) {
      errors = {
        headers: 'At least one filter is required',
        body: 'At least one filter is required',
        path: 'At least one filter is required',
        query: 'At least one filter is required',
      };
    }
    return errors;
  },
  transform: async (props) => {
    if (!props.transformation?.name) {
      return {
        transformation: 'Transformation required',
      };
    }
    return {};
  },
  retry: ({ count, interval, strategy, response_status_codes }) => {
    const errors: {
      count?: string;
      interval?: string;
      strategy?: string;
      retries_error?: string;
      response_status_codes?: string;
    } = {};
    if (!count) {
      errors.count = 'Count required';
    }
    if (!interval) {
      errors.interval = 'Interval required';
    }
    if (!strategy) {
      errors.strategy = 'Strategy required';
    }
    if (count > 50) {
      errors.count = 'Retry count must be equal or less than 50';
    }
    if (response_status_codes) {
      const error = validateHttpCodes(response_status_codes);
      if (error) {
        errors.response_status_codes = error;
      }
    }

    if (count && interval && strategy) {
      const max_retry_distance = 1000 * 60 * 60 * 24 * 7;
      let distance;
      if (!strategy || strategy === 'linear') {
        distance = count * interval;
      } else {
        distance = Math.pow(2, count - 1) * interval;
      }
      if (distance > max_retry_distance) {
        errors.strategy =
          "The retry count, interval and strategy can't lead to retries further then 1 week in the future.";
      }
    }
    return errors;
  },
  delay: ({ delay }) => {
    const errors: {
      delay?: string;
    } = {};

    if (!delay) {
      errors.delay = 'Delay required';
    }

    if (delay < 1000) {
      errors.delay = "Delay can't be shorter than 1 second";
    } else if (delay > 1000 * 60 * 60 * 24) {
      errors.delay = "Delay can't be over 24 hours";
    }

    return errors;
  },
  deduplicate: ({ window, field_mode, fields }) => {
    const errors: {
      window?: string;
      fields?: string;
    } = {};
    if (!window) {
      errors.window = 'Window required';
    } else if (window < 1000) {
      errors.window = "Deduplication window can't be shorter than 1 second";
    } else if (window > 3600000) {
      errors.window = "Deduplication window can't be longer than 1 hour";
    }
    if ((field_mode === 'include' || field_mode === 'exclude') && !fields) {
      errors.fields = 'Specify at least 1 field';
    }
    if (fields) {
      const valid_prefixes = ['headers', 'body', 'query', 'path'];
      // Regex to validate field paths with optional array notation
      // Field must NOT end with array notation - must have a property after [*] or [n]
      const field_path_pattern =
        /^(headers|body|query|path)(\.[^.[\]]+(\[\*\]|\[\d+\])?)*(\.[^.[\]]+)?$/;

      for (const field of fields.split(',').map((field) => field.trim())) {
        // Check if field ends with array notation (invalid)
        if (/(\[\*\]|\[\d+\])$/.test(field)) {
          errors.fields = `Invalid field: ${field}. Array notation [*] or [n] must be followed by a property (e.g., "body.items[*].sku" not "body.items[*]")`;
          break;
        }

        // Check if field matches valid pattern
        if (!field_path_pattern.test(field)) {
          const prefix = field.split('.')[0];
          if (!valid_prefixes.includes(prefix)) {
            errors.fields = `Invalid field: ${field}. Must start with: ${valid_prefixes.join(', ')}`;
          } else {
            errors.fields = `Invalid field syntax: ${field}. Use dot notation (e.g., "body.user.id") with optional array notation (e.g., "body.items[*].sku" or "body.items[0].name")`;
          }
          break;
        }
      }
    }
    return errors;
  },
};

export const validateFormRules = async (rules: any, HookdeckAPI: HookdeckAPI) => {
  const errors = await Promise.all(rules.map((r) => validate[r.type](r, HookdeckAPI)));
  return errors.filter((r) => Object.keys(r).length > 0).length > 0 ? errors : null;
};

const preprocessWebhookFilterProperty = (property?: WebhookFilterProperty) => {
  if (property === null || typeof property === 'undefined') {
    return '';
  }
  return JSON.stringify(property, null, '\t');
};

const preprocess = {
  retry: (rule: RetryRule) => {
    return {
      ...rule,
      response_status_codes: rule.response_status_codes
        ? rule.response_status_codes.join(', ')
        : '',
    };
  },
  deduplicate: (rule: DeduplicateRule) => {
    let field_mode = 'all';
    if (rule.include_fields && rule.include_fields.length > 0) {
      field_mode = 'include';
    } else if (rule.exclude_fields && rule.exclude_fields.length > 0) {
      field_mode = 'exclude';
    }
    return {
      type: rule.type,
      field_mode,
      fields: rule.include_fields?.join(', ') || rule.exclude_fields?.join(', '),
      window: rule.window,
    };
  },
  filter: (rule: FilterRule) => {
    return {
      ...rule,
      headers: preprocessWebhookFilterProperty(rule?.headers),
      body: preprocessWebhookFilterProperty(rule?.body),
      path: preprocessWebhookFilterProperty(rule?.path),
      query: preprocessWebhookFilterProperty(rule?.query),
    };
  },
};

export const preprocessFormRules = (rules: any) => {
  return rules.map((r) => (preprocess[r.type] ? preprocess[r.type](r) : r));
};

const postprocess = {
  retry: (input): RetryRule | undefined => {
    if (input.response_status_codes) {
      const rules: string[] = input.response_status_codes.split(',').map((rule) => rule.trim());
      input.response_status_codes = rules.filter((rule) => complete_rule_pattern.test(rule));
    } else {
      input.response_status_codes = undefined;
    }

    return input;
  },
  filter: (input): FilterRule | undefined => {
    delete input.event_id;
    delete input.test_input;

    const filter = {
      ...input,
      headers: input.headers ? JSON.parse(input.headers) : undefined,
      body: input.body ? JSON.parse(input.body) : undefined,
      path: input.path ? JSON.parse(input.path) : undefined,
      query: input.query ? JSON.parse(input.query) : undefined,
    };

    if (
      filter.headers === undefined &&
      filter.body === undefined &&
      filter.path === undefined &&
      filter.query === undefined
    ) {
      return undefined;
    }
    return filter;
  },
  transform: (input): TransformRule | undefined => {
    if (!input.transformation?.name) {
      return undefined;
    }
    if (input.transformation.env && Array.isArray(input.transformation.env)) {
      input.transformation.env = input.transformation.env.reduce(
        (object, [key, value]) => ({ ...object, [key]: value }),
        {},
      );
    }
    delete input.input;
    return input;
  },
  deduplicate: (input): DeduplicateRule | undefined => {
    if (input.field_mode === 'include') {
      input.include_fields = input.fields.split(',').map((field) => field.trim());
      delete input.fields;
    } else if (input.field_mode === 'exclude') {
      input.exclude_fields = input.fields.split(',').map((field) => field.trim());
      delete input.fields;
    }
    delete input.field_mode;
    return input;
  },
};

export const postprocessFormRules = (rules: any) => {
  return rules.map((r) => (postprocess[r.type] ? postprocess[r.type](r) : r)).filter((r) => !!r);
};

export const RULE_TYPES = {
  transform: {
    label: 'Transform',
    icon: 'transformation',
    description: 'Modify the request body, headers, query, or path by using a Javascript function.',
    docs: LINKS.product_docs.transformations,
    getLabel: (rule, transformation: Transformation): string => {
      return transformation?.name;
    },
  },
  filter: {
    label: 'Filter',
    icon: 'filter',
    description:
      'Filter and route events conditionally based on the contents of their body, headers, query, or path.',
    docs: LINKS.product_docs.filters,
    getLabel: (rule: FilterRule): string => {
      const active_keys = Object.keys(rule).filter((rule) => rule !== 'type');
      return `Filtering on ${active_keys.length === 0 ? 'nothing' : active_keys.join(', ')} `;
    },
  },
  delay: {
    label: 'Delay',
    icon: 'schedule',
    description: 'Add a delay between the time an event is received and is delivered.',
    docs: LINKS.product_docs.connection_delay,
    getLabel: (rule: DelayRule): string => {
      const [interval, unit] = getMsIntervalByUnit(rule.delay, 'delay');
      return `Delaying for ${interval} ${interval === 1 ? unit.slice(0, -1) : unit}`;
    },
  },
  retry: {
    label: 'Retry',
    icon: 'retry',
    description: 'Configure an automatic retry strategy for event delivery failures.',
    docs: LINKS.product_docs.retries,
    getLabel: (rule: RetryRule): string => {
      if (!rule.strategy || rule.count === 0) {
        return `Retries disabled`;
      }
      const [interval, unit] = getMsIntervalByUnit(rule.interval, 'retry');
      return `Retry ${RETRY_STATEGY_LABELS[rule.strategy].toLowerCase()} every ${interval} ${
        interval === 1 ? unit.slice(0, -1) : unit
      } up to ${rule.count}
      times`;
    },
  },
  deduplicate: {
    label: 'Deduplicate',
    icon: 'deduplicate',
    description:
      'Ignore redundant events by comparing them against previously processed events within a time window.',
    docs: LINKS.product_docs.deduplicate,
    getLabel: (rule: DeduplicateRule): string => {
      const [interval, unit] = getMsIntervalByUnit(rule.window, 'deduplicate');
      let mode = 'all fields';
      if (rule.include_fields && rule.include_fields.length > 0) {
        mode = `${rule.include_fields.length} field${rule.include_fields.length > 1 ? 's' : ''}`;
      } else if (rule.exclude_fields && rule.exclude_fields.length > 0) {
        mode = `excluding ${rule.exclude_fields.length} field${rule.exclude_fields.length > 1 ? 's' : ''}`;
      }
      return `Deduplicate based on ${mode} within ${interval} ${interval === 1 ? unit.slice(0, -1) : unit}`;
    },
  },
} as const;
